"use client";

import { ArrowDown, Compass } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RevenueTimeline() {
  const milestones = [{
    marker: "S-06",
    title: "Revenue Audit",
    description: "A comprehensive analysis of your current OTA performance and pricing architecture. We identify leakage points and untapped revenue opportunities.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_analysis.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_analysis.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_analysis.png-wvc-sizes",
    alt: "Revenue manager reviewing hotel performance data on dashboard"
  }, {
    marker: "S-05",
    title: "OTA Strategy",
    description: "Five weeks of intensive OTA optimization. We refine your listings, update content, and implement dynamic pricing rules to capture high-value segments.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_strategy_optimization.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_strategy_optimization.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_strategy_optimization.webp-wvc-sizes",
    alt: "Digital marketing strategy session for hotel revenue growth"
  }, {
    marker: "S-04",
    title: "Tech Stack Audit",
    description: "Reviewing your PMS, Channel Manager, and Booking Engine integration. We ensure your tech stack supports seamless data flow and direct booking conversion.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_stack_integration.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_stack_integration.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_stack_integration.webp-wvc-sizes",
    alt: "Hospitality technology stack integration and management"
  }, {
    marker: "S-03",
    title: "Rate Parity Control",
    description: "Every channel is monitored for rate integrity. We eliminate discrepancies that drive guests away from your direct booking engine.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/rate_parity_monitoring.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/rate_parity_monitoring.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/rate_parity_monitoring.png-wvc-sizes",
    alt: "Monitoring rate parity across multiple booking channels"
  }, {
    marker: "S-02",
    title: "Demand Forecasting",
    description: "Advanced predictive modeling replicates market demand patterns. Your property learns to adjust rates automatically to maximize RevPAR.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/demand_forecasting_data.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/demand_forecasting_data.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/demand_forecasting_data.png-wvc-sizes",
    alt: "Data visualization showing hotel demand forecasting trends"
  }, {
    marker: "S-01",
    title: "Growth Execution",
    description: "Full-scale implementation of your revenue roadmap. Our team convenes for the final tactical review to ensure sustained growth and market dominance.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_growth_execution.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_growth_execution.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_growth_execution.png-wvc-sizes",
    alt: "Hotel revenue management team reviewing growth metrics"
  }];
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.5
  });
  return <section data-section-id="3114" id="solutiontimeline" className="bg-background text-foreground py-32 md:py-[200px] px-6 md:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 mb-24 md:mb-32">
          <div className={`lg:col-span-6 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="font-default text-xs tracking-[0.25em] uppercase mb-8" style={{
            color: "var(--secondary)"
          }}>
              Phase IV — Revenue Optimization
            </p>
            <h2 className="font-default text-5xl md:text-6xl lg:text-[76px] leading-[1.05] tracking-tight text-foreground">
              Six steps
              <br />
              <span className="italic font-light" style={{
              color: "var(--muted-foreground)"
            }}>
                to maximize
              </span>{" "}
              your revenue.
            </h2>
          </div>
          <div className={`lg:col-span-5 lg:col-start-8 flex items-end transition-all duration-1000 delay-200 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="font-default text-base md:text-lg leading-relaxed" style={{
            color: "var(--muted-foreground)"
          }}>
              Revenue growth is a discipline of precision. At HotelRevBaBa, we follow a structured, data-driven roadmap — six steps of measured, deliberate work where every audit, every listing, and every pricing rule compounds into the consistent profitability your property deserves.
            </p>
          </div>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px md:-translate-x-1/2" style={{
          background: `linear-gradient(to bottom, transparent 0%, var(--secondary) 8%, var(--secondary) 92%, transparent 100%)`
        }} aria-hidden="true">
            <div className="absolute inset-0 animate-pulse" style={{
            background: `linear-gradient(to bottom, transparent, var(--secondary), transparent)`,
            opacity: 0.4
          }} />
          </div>

          {}
          <div className="space-y-24 md:space-y-32">
            {milestones.map((milestone, i) => {
            const isOdd = i % 2 === 0;
            return <TimelineMilestone key={i} data-index={i} milestone={milestone} index={i} isLeft={isOdd} />;
          })}
          </div>

          {}
          <div ref={footerRef} className={`relative mt-24 md:mt-32 flex flex-col items-center transition-all duration-1000 ease-out ${footerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative z-10 flex items-center justify-center w-16 h-16 rounded-lg" style={{
            backgroundColor: "var(--background)",
            border: `1px solid var(--secondary)`,
            boxShadow: `0 0 30px hsla(43, 46%, 55%, 0.3)`
          }}>
              <Compass className="w-7 h-7" style={{
              color: "var(--secondary)"
            }} strokeWidth={1.5} />
            </div>
            <div className="mt-6 flex items-center gap-3">
              <p className="font-default text-xs tracking-[0.3em] uppercase" style={{
              color: "var(--secondary)"
            }}>
                Start Your Growth
              </p>
              <ArrowDown className="w-3.5 h-3.5" style={{
              color: "var(--secondary)"
            }} strokeWidth={1.5} />
            </div>
          </div>
        </div>
      </div>
    </section>;
}
type Milestone = {
  marker: string;
  title: string;
  description: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
};
function TimelineMilestone({
  milestone,
  index,
  isLeft,
  ...rest
}: {
  milestone: Milestone;
  index: number;
  isLeft: boolean;
  "data-index"?: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  return <div ref={ref} data-index={rest["data-index"] ?? index} className="relative">
      {}
      <div className="absolute left-8 md:left-1/2 top-2 md:-translate-x-1/2 z-10" aria-hidden="true">
        <div className="relative">
          <div className={`w-3.5 h-3.5 rounded-lg transition-all duration-700 ${isVisible ? "scale-100" : "scale-50"}`} style={{
          backgroundColor: "var(--secondary)",
          boxShadow: isVisible ? `0 0 0 4px hsla(43, 46%, 55%, 0.15), 0 0 20px hsla(43, 46%, 55%, 0.6)` : "none"
        }} />
          {isVisible && <div className="absolute inset-0 rounded-lg animate-ping" style={{
          backgroundColor: "var(--secondary)",
          opacity: 0.3
        }} />}
        </div>
      </div>

      {}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-0">
        {}
        <div className={`pl-20 md:pl-0 md:pr-20 ${isLeft ? "md:col-start-1 md:text-right" : "md:col-start-2 md:hidden"}`}>
          {isLeft && <MilestoneContent milestone={milestone} isVisible={isVisible} alignRight={true} />}
        </div>

        {}
        <div className={`pl-20 md:pl-20 md:pr-0 ${!isLeft ? "md:col-start-2" : "md:col-start-2 md:hidden"}`}>
          {!isLeft && <MilestoneContent milestone={milestone} isVisible={isVisible} alignRight={false} />}
        </div>
      </div>
    </div>;
}
function MilestoneContent({
  milestone,
  isVisible,
  alignRight
}: {
  milestone: Milestone;
  isVisible: boolean;
  alignRight: boolean;
}) {
  return <div className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-x-0" : alignRight ? "opacity-0 -translate-x-8" : "opacity-0 translate-x-8"}`}>
      <p className="font-default italic text-3xl md:text-4xl mb-3" style={{
      color: "var(--secondary)"
    }}>
        {milestone.marker}
      </p>
      <h3 className="font-default text-2xl md:text-[32px] leading-tight mb-4 text-foreground">
        {milestone.title}
      </h3>
      <p className="font-default text-base leading-relaxed mb-6 max-w-md" style={{
      color: "var(--muted-foreground)",
      marginLeft: alignRight ? "auto" : 0
    }}>
        {milestone.description}
      </p>
      <div className={`overflow-hidden rounded-lg ${alignRight ? "md:ml-auto" : ""}`} style={{
      width: "100%",
      maxWidth: "320px",
      aspectRatio: "4 / 3",
      border: `1px solid var(--border)`
    }}>
        <img src={milestone.image} data-wvc-srcset={milestone.srcset} data-wvc-sizes={milestone.sizes} alt={milestone.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
      </div>
    </div>;
}